import React, {useState, useEffect} from 'react';
import { Alert, Button, Linking, Share, StyleSheet, TextInput, TouchableOpacity, View } from 'react-native'

import tw from '../../../../styles/tailwind';
import Text from '../../../../components/Text';
import IcomoonIcon from '../../../../components/IcomoonIcon';
import { Illustration } from '../../../../components/Illustration';
import Modal from '../../../../components/Modal';
import BottomSheet from '../../../../components/BottomSheet';
import Input from '../../../../components/Forms/Input';
import { useForm } from 'react-hook-form';
import IconButton from '../../../../components/IconButton';
import useFormSubmit from '../../../../hooks/useFormSubmit';
import Select from '../../../../components/Forms/Select';
import api from '../../../../utils/api';
import { Image } from 'expo-image';
import QRCode from 'react-native-qrcode-svg';
import { Ionicons } from '@expo/vector-icons';


type ResidentFormModalProps = {
  resident: null | Resident,
  onRequestClose?:() => void,
  onResidentCreated?:(resident: Resident) => void,
  onResidentEdited?:(resident: Resident) => void,
  onResidentDeleted?:() => void,
  communityId: string,
  unitId: string,
  visible: boolean,
}

export default function ResidentFormModal({ 
  resident = null,
  onRequestClose,
  onResidentCreated,
  onResidentEdited,
  onResidentDeleted,
  communityId,
  unitId,
  visible,
}: ResidentFormModalProps) {
  const { control, handleSubmit, reset, formState: {isDirty} } = useForm();
  const [creatingInvitation, setCreatingInvitation] = useState(false);
  const [errorMessage, setErrorMessage] = useState();
  const [isEditing, setIsEditing] = useState(false);
  const [invitationData, setInvitationData] = useState();

  const { submitForm, isSubmitting } = useFormSubmit({
    onSuccess: (response) => {
      if(! resident) {
        onResidentCreated?.(response)
      } else{
        onResidentEdited?.(response)
      }
    },
    onError: (error) => {
      // console.log("error submit", error);
      setErrorMessage(error?.message)
    },
    successToastMessage: ! resident ? "Residente creado" : "Residente actualizado",
    method: ! resident ? "post" : "put",
    endpoint: `/communities/${communityId}/units/${unitId}/residents${resident ? `/${resident.uuid}` : ''}`,
  })


  const { submitForm: submitDeletion, isSubmitting: isDeleting} = useFormSubmit({
    onSuccess: onResidentDeleted,
    onError: (error) => { },
    successToastMessage: "Residente eliminado",
    method: "delete",
    endpoint: `/communities/${communityId}/units/${unitId}/residents/${resident?.uuid}`,
  })

  useEffect(() => {
    reset({})
    setErrorMessage()
    setIsEditing(false)
  }, [visible]);

  useEffect(() => {
    if (resident){
      reset({
        type: resident.type,
        name: resident.name,
        phone_number: resident.user?.phone_number || "",
        email: resident.user?.email || "",
      })

      if (!resident?.user?.is_registered){
        createInvitation()
      }
    }
  }, [resident]);


  function _submitForm(data){
    setErrorMessage()
    submitForm(data)
  }


  // TODO: ABSTRACT_USER_INVITATION
  function createInvitation(showQRPage=false){
    setCreatingInvitation(true)

    api.post(`/residents/${resident?.uuid}/invitation`)
    .then(({data}) => {
      setCreatingInvitation(false)
      
      // const shareMsg = !showQRPage ? data.message : `${data.url}?qr=1`
      // shareInvitation(shareMsg)
      setInvitationData(data)
    })
    .catch((response) => {
      setCreatingInvitation(false)
      console.log("error", response); 
    })
  }

  async function shareInvitation(message:string){
    try {
      const result = await Share.share({
        message,
      });
      if (result.action === Share.sharedAction) {
        if (result.activityType) {
          // shared with activity type of result.activityType
        } else {
          // shared
        }
      } else if (result.action === Share.dismissedAction) {
        // dismissed
      }
    } catch (error: any) {
      Alert.alert(error.message);
    }
  }


  // eliminar residente
  function confirmResidentDeletion(){
    Alert.alert('¿Eliminar este residente?', 'Una vez eliminado quedará deshabilitado y no podrá acceder usar la app en este fraccionamiento', [
      {
        text: 'Cancelar',
        onPress: () => { },
        style: 'cancel',
      },
      { text: 'Eliminar', onPress: () => submitDeletion({}) },
    ]);
  }


  return (
    <BottomSheet onRequestClose={onRequestClose} visible={visible} height={580}>
      {
        (!resident || isEditing) &&
        <View style={tw`pt-5 px-5`}>
          <Text style={tw`mb-4 text-lg font-600 text-secondary`}>{resident ? "Editar" : "Registrar"} residente</Text>
          
          {/* Nombre */}
          <View style={tw`mb-0`}>
            <Text style={tw`text-gray-500`}>Nombre del residente</Text>

            <Input control={control} name='name' rules={{required: true}} />
          </View>
          

          {/* Teléfono */}
          <View style={tw`mb-0`}>
            <Text style={tw`text-gray-500`}>Teléfono</Text>

            <Input
              control={control}
              name='phone_number'
              placeholder='10 dígitos'
              keyboardType="phone-pad"
              maxLength={10}
              rules={{ 
                // required: "El teléfono es requerido",
                pattern: { value: /\d{10}/, message: "El teléfono debe tener 10 dígitos" } 
              }}
            />
          </View>

          
          {/* Tipo */}
          <View style={tw`mb-0`}>
            <Text style={tw`text-gray-500`}>Tipo de residente</Text>

            <Select
              control={control}
              name='type'
              placeholder={{ value: "", label: "Seleccionar tipo" }}
              items={[
                { value: "owner", label: "Propietario" },
                { value: "resident", label: "Residente" },
                { value: "tenant", label: "Arrendatario" },
              ]}
              rules={{required:true}}
            />
          </View>


          {/* Teléfono */}
          <View style={tw`mb-0`}>
            <Text style={tw`text-gray-500`}>Correo electrónico</Text>

            <Input
              control={control}
              name='email'
              placeholder={resident ? "" : 'opcional'}
              keyboardType="email-address"
              rules={{ pattern: { value: /^\S+@\S+$/, message: "Correo no válido" } }}
            />
          </View>

          {
            errorMessage &&
              <Text style={tw`text-center my-3 text-red`}>{errorMessage}</Text>
          }


          <View style={tw`row justify-between`}>
            <View />

            <IconButton
              disabled={isSubmitting || !isDirty}
              title={isSubmitting ? "Guardando" : "Guardar"}
              style={tw`self-end`}
              onPress={handleSubmit(_submitForm)}
            />
          </View>

        </View>
      }


      {
        (resident && !isEditing) &&
          <View style={tw`pt-5 px-5`}>
            <Text style={tw`mb-4 text-lg font-600 text-secondary`}>Datos del residente</Text>

            <View style={tw`mb-4`}>
              <Text style={tw`text-lg text-gray-600`}>{resident.name}</Text>

              {
                resident.user?.is_registered &&
                  <View style={tw`row`}>
                    <Illustration name="residentes" width={16} height={16} style={tw`mr-2`} />
                    <Text style={tw`text-primary`}>Con cuenta DingDong</Text>
                  </View>
              }
            </View>

            {
              resident.user?.phone_number &&
                <Text style={tw`text-lg text-gray-600`} onPress={() => Linking.openURL(`tel:${resident.user?.phone_number}`)}>{resident.user.phone_number}</Text>
            }

            {
              resident.user?.email &&
                <Text style={tw`text-base text-gray-600`}>{resident.user.email}</Text>
            }
            
            {
              !resident?.user?.is_registered &&
                <View style={tw``}>
                  {
                    invitationData &&
                      <View style={tw`items-center py-10`}>
                        <QRCode
                          value={invitationData.url}
                          size={200}
                        />

                        <IconButton
                          disabled={creatingInvitation}
                          title={"Enviar link"}
                          style={tw`mt-10`}
                          onPress={() => {
                            console.log(invitationData)
                            console.log(invitationData.url)
                            shareInvitation(invitationData.message)
                          }}
                          // onLongPress={() => createInvitation(true)}
                          // delayLongPress={750}
                          // icon={<Ionicons name='send-outline' />}
                        />
                      </View>
                      
                  }
                </View>
            }
          </View>
      }

      {
        resident &&
        <View style={tw`row justify-between mt-4 px-6`}>
          <TouchableOpacity style={tw``} onPress={confirmResidentDeletion} disabled={isDeleting}>
            <Text style={tw.style(`text-red`, isDeleting && `opacity-50`)}>{isDeleting ? "Eliminando residente..." : "Eliminar residente"}</Text>
          </TouchableOpacity>

          {
              (!isEditing && !resident?.user?.is_registered) ?
                <IconButton
                  title={"Editar"}
                  color='white'
                  textColor='primary'
                  onPress={() => setIsEditing(true)}
                />
            :
              <View></View>
          }
        </View>
      }
    </BottomSheet>
  );
}
